import { useState, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import {
  Crown, Diamond, Zap, Check, Presentation, Cloud, Sparkles,
  ChevronDown, ArrowRight, ArrowLeft, Star, BadgeCheck, TrendingUp
} from "lucide-react"
import { PlanCheckout } from "./PlanCheckout"
import "./LicensesManagement.css"
import { RibbonBackground } from "@/features/messenger"
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'

interface Plan {
  id: string
  name: string
  description: string
  icon: React.ReactNode
  iconBg: string
  gradient: string
  accentColor: string
  monthlyPrice: number | null
  yearlyPrice: number | null
  popular?: boolean
  isFree?: boolean
  isBusiness?: boolean
  features: string[]
  featureHighlight?: string
}

interface AddOn {
  id: string
  name: string
  description: string
  learnMore: string
  icon: React.ReactNode
  iconBg: string
  gradient: string
  monthlyPrice: number
  yearlyPrice: number
}

export default function PricingPage() {
  const t = useTranslation()
  const navigate = useNavigate()
  const [billingCycle] = useState<"monthly" | "yearly">("yearly") // Fixed to yearly only
  const [licenseType, setLicenseType] = useState<"Concurrent" | "Host">("Host")
  const [showCheckout, setShowCheckout] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<any>(null)
  const [showCurrencyMenu, setShowCurrencyMenu] = useState(false)
  const [currency, setCurrency] = useState("USD")
  const [hoveredPlan, setHoveredPlan] = useState<string | null>(null)
  const currencyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (currencyRef.current && !currencyRef.current.contains(e.target as Node)) {
        setShowCurrencyMenu(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const hostPlans: Plan[] = [
    {
      id: "free", name: "Free", description: "Get started at no cost",
      icon: <Star className="w-6 h-6 text-slate-400" />,
      iconBg: "bg-slate-100 dark:bg-slate-800",
      gradient: "from-slate-200 to-slate-300",
      accentColor: "text-slate-400",
      monthlyPrice: 0, yearlyPrice: 0, isFree: true,
      featureHighlight: "100 Participants",
      features: ["Single Host", "Up to 100 Participants", "Whiteboard", "File sharing", "Presenter mode", "Video share", "Recordings", "Document share", "Chat"],
    },
    {
      id: "standard", name: "Standard", description: "Perfect for growing teams",
      icon: <Crown className="w-6 h-6 text-amber-400" />,
      iconBg: "bg-amber-50 dark:bg-amber-900/20",
      gradient: "from-amber-200 to-amber-300",
      accentColor: "text-amber-400",
      monthlyPrice: 8, yearlyPrice: 80,
      featureHighlight: "100 Participants",
      features: ["Single Host", "Up to 100 Participants", "Whiteboard", "File sharing", "Presenter mode", "Video share", "Recordings", "Document share", "Chat"],
    },
    {
      id: "pro", name: "Pro", description: "Best for scaling businesses",
      icon: <Zap className="w-6 h-6 text-white" />,
      iconBg: "bg-card/20 backdrop-blur-sm",
      gradient: "from-purple-500 via-purple-600 to-indigo-600",
      accentColor: "text-purple-400",
      monthlyPrice: 13, yearlyPrice: 125, popular: true,
      featureHighlight: "300 Participants",
      features: ["Single Host", "Up to 300 Participants", "Whiteboard", "File sharing", "Presenter mode", "Video share", "HD Recordings", "Document share", "Chat", "Priority Support"],
    },
    {
      id: "business", name: "Business", description: "Enterprise-grade power",
      icon: <Diamond className="w-6 h-6 text-purple-400" />,
      iconBg: "bg-purple-50 dark:bg-purple-900/20",
      gradient: "from-purple-200 to-violet-300",
      accentColor: "text-purple-400",
      monthlyPrice: null, yearlyPrice: null, isBusiness: true,
      featureHighlight: "500+ Participants",
      features: ["Unlimited Hosts", "Up to 500 Participants", "Whiteboard", "File sharing", "Presenter mode", "Video share", "Cloud Recordings", "Document share", "Chat", "Dedicated Support"],
    },
  ]

  const concurrentPlans: Plan[] = [
    {
      id: "standard", name: "Standard", description: "Concurrent rooms for teams",
      icon: <Crown className="w-6 h-6 text-amber-400" />,
      iconBg: "bg-amber-50 dark:bg-amber-900/20",
      gradient: "from-amber-200 to-amber-300",
      accentColor: "text-amber-400",
      monthlyPrice: 200, yearlyPrice: 2000,
      featureHighlight: "Up to 30 Rooms",
      features: ["Concurrent Rooms", "Up to 100 Participants/room", "Whiteboard", "File sharing", "Presenter mode", "Video share", "Recordings", "Document share", "Chat"],
    },
    {
      id: "pro", name: "Pro", description: "Scale across your organisation",
      icon: <Zap className="w-6 h-6 text-white" />,
      iconBg: "bg-card/20 backdrop-blur-sm",
      gradient: "from-purple-500 via-purple-600 to-indigo-600",
      accentColor: "text-purple-400",
      monthlyPrice: 600, yearlyPrice: 6000, popular: true,
      featureHighlight: "Up to 100 Rooms",
      features: ["Concurrent Rooms", "Up to 300 Participants/room", "Whiteboard", "File sharing", "Presenter mode", "Video share", "HD Recordings", "Document share", "Chat", "Priority Support"],
    },
    {
      id: "business", name: "Business", description: "Enterprise concurrent rooms",
      icon: <Diamond className="w-6 h-6 text-purple-400" />,
      iconBg: "bg-purple-50 dark:bg-purple-900/20",
      gradient: "from-purple-100 to-purple-200",
      accentColor: "text-purple-400",
      monthlyPrice: null, yearlyPrice: null, isBusiness: true,
      featureHighlight: "Unlimited Rooms",
      features: ["Unlimited Concurrent Rooms", "500+ Participants/room", "Whiteboard", "File sharing", "Presenter mode", "Video share", "Cloud Recordings", "Document share", "Chat", "SLA Support"],
    },
  ]

  const addOns: AddOn[] = [
    {
      id: "cloud-storage", name: "Cloud Storage",
      description: "Store, stream, and download your video recordings directly from the InMeet Cloud.",
      learnMore: "Learn More",
      icon: <Cloud className="w-7 h-7 text-cyan-400" />,
      iconBg: "bg-cyan-50 dark:bg-cyan-900/20",
      gradient: "from-cyan-200 to-blue-300",
      monthlyPrice: 9, yearlyPrice: 90,
    },
    {
      id: "webinar", name: "Webinar",
      description: "Host engaging webinars with up to 5,000 attendees and powerful engagement tools.",
      learnMore: "Learn More",
      icon: <Presentation className="w-7 h-7 text-orange-400" />,
      iconBg: "bg-orange-50 dark:bg-orange-900/20",
      gradient: "from-orange-200 to-rose-300",
      monthlyPrice: 84, yearlyPrice: 840,
    },
    {
      id: "ai-summary", name: "AI Summary",
      description: "Automatically generate intelligent meeting summaries and action items with AI.",
      learnMore: "Learn More",
      icon: <Sparkles className="w-7 h-7 text-violet-400" />,
      iconBg: "bg-violet-50 dark:bg-violet-900/20",
      gradient: "from-violet-200 to-purple-300",
      monthlyPrice: 20, yearlyPrice: 200,
    },
  ]

  const plans = licenseType === "Host" ? hostPlans : concurrentPlans
  const getPrice = (plan: Plan) => billingCycle === "monthly" ? plan.monthlyPrice : plan.yearlyPrice
  const getAddOnPrice = (a: AddOn) => billingCycle === "monthly" ? a.monthlyPrice : a.yearlyPrice

  if (showCheckout && selectedPlan) {
    return <PlanCheckout plan={selectedPlan} billingCycle={billingCycle} onBack={() => setShowCheckout(false)} />
  }

  return (
    <RibbonBackground className="w-full">
    <div className="w-full h-full overflow-y-auto">
      <div className="max-w-7xl mx-auto px-4 pb-6 pt-2 space-y-4">

        {/* ─── Breadcrumb ──────────────────────────────── */}
        <div className="flex items-center gap-2 text-sm">
          <button onClick={() => navigate("/settings/licenses")}
            className="flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors font-medium">
            <ArrowLeft className="w-3.5 h-3.5" /> {t("licenses.title")}
          </button>
          <span className="text-muted-foreground">/</span>
          <span className="text-foreground font-semibold">{t("licenses.pricing")}</span>
        </div>

        {/* ─── Hero ────────────────────────────────────── */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white tracking-tight">
            {t("licenses.simpleTransparentPricing")}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-base max-w-lg mx-auto">
            {t("licenses.choosePlanDescription")}
          </p>
        </div>

        {/* ─── Controls Bar ────────────────────────────── */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* License type pill toggle */}
          <div className="flex bg-slate-100/80 dark:bg-slate-800/50 p-1 rounded-xl">
            {(["Host", "Concurrent"] as const).map((type) => (
              <button
                key={type}
                onClick={() => setLicenseType(type)}
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  licenseType === type
                    ? "bg-card dark:bg-slate-700 text-slate-900 dark:text-foreground/80 dark:text-white shadow-sm"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                }`}
              >
                {type === "Host" ? t("licenses.host") : t("licenses.concurrent")}
              </button>
            ))}
          </div>

          {/* Currency selector only */}
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <div className="flex items-center gap-2 bg-slate-100/80 dark:bg-slate-800/50 px-4 py-2 rounded-xl">
              <span className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("licenses.yearlyBilling")}</span>
              <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full">{t("licenses.yearlyDiscount")}</span>
            </div>
            <div className="relative" ref={currencyRef}>
              <button
                onClick={() => setShowCurrencyMenu(!showCurrencyMenu)}
                className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-300 bg-slate-100/80 dark:bg-slate-800/50 px-3 py-2 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                {currency} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showCurrencyMenu ? "rotate-180" : ""}`} />
              </button>
              {showCurrencyMenu && (
                <div className="absolute right-0 top-10 bg-card border border-border rounded-xl shadow-xl z-20 min-w-[90px] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                  {["USD", "INR", "EUR"].map((c) => (
                    <button key={c} onClick={() => { setCurrency(c); setShowCurrencyMenu(false) }}
                      className={`block w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${currency === c ? "text-primary bg-primary/5" : "hover:bg-muted text-slate-700 dark:text-slate-300"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ─── Plan Cards ──────────────────────────────── */}
        <div className={`grid gap-5 ${plans.length === 4 ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-3"}`}>
          {plans.map((plan, idx) => {
            const price = getPrice(plan)
            const isPro = plan.popular
            const isHovered = hoveredPlan === plan.id

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1, duration: 0.5 }}
                onMouseEnter={() => setHoveredPlan(plan.id)}
                onMouseLeave={() => setHoveredPlan(null)}
                className={`relative rounded-2xl flex flex-col overflow-hidden transition-all duration-300 cursor-default
                  ${isPro
                    ? "shadow-2xl shadow-purple-500/25 scale-[1.02] ring-2 ring-purple-400/30 shimmer-card"
                    : `shadow-sm hover:shadow-xl hover:-translate-y-1 ${isHovered ? "ring-1 ring-border" : ""}`
                  }
                `}
              >
                {/* Pro: full gradient background with enhanced design */}
                {isPro ? (
                  <>
                    <div className={`absolute inset-0 bg-gradient-to-br ${plan.gradient}`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
                    {/* Decorative elements */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-card/10 rounded-full blur-3xl" />
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-card/5 rounded-full blur-2xl" />
                  </>
                ) : (
                  <div className="absolute inset-0 bg-card dark:bg-card" />
                )}


                {/* Popular badge */}
                {isPro && (
                  <div className="absolute top-3.5 right-3.5 z-10">
                    <span className="flex items-center gap-1 text-[10px] font-bold text-white border border-white/30 bg-card/15 backdrop-blur-md px-3 py-1.5 rounded-full tracking-wide shadow-lg">
                      <TrendingUp className="w-2.5 h-2.5" /> {t("licenses.mostPopular")}
                    </span>
                  </div>
                )}

                {/* Gradient top accent for non-pro cards */}
                {!isPro && (
                  <div className={`h-1 w-full bg-gradient-to-r ${plan.gradient}`} />
                )}

                <div className={`relative z-10 p-6 flex-1 flex flex-col`}>
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 shadow-lg ${isPro ? plan.iconBg : plan.iconBg}`}>
                    {plan.icon}
                  </div>

                  {/* Name & description */}
                  <h3 className={`text-xl font-bold mb-1 ${isPro ? "text-white" : "text-slate-900 dark:text-foreground/80 dark:text-white"}`}>
                    {plan.name}
                  </h3>
                  <p className={`text-xs leading-relaxed mb-4 ${isPro ? "text-white/70" : "text-slate-500 dark:text-slate-400"}`}>
                    {plan.description}
                  </p>

                  {/* Highlight chip */}
                  {plan.featureHighlight && (
                    <div className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 w-fit shadow-sm
                      ${isPro ? "bg-card/20 text-white backdrop-blur-sm border border-white/20" : `bg-gradient-to-r ${plan.gradient} bg-clip-text text-transparent border border-current/20`}`}>
                      <BadgeCheck className={`w-3 h-3 ${isPro ? "text-white" : plan.accentColor}`} />
                      <span className={isPro ? "" : plan.accentColor}>{plan.featureHighlight}</span>
                    </div>
                  )}

                  {/* Price */}
                  <div className="mb-5">
                    {plan.isBusiness ? (
                      <div>
                        <p className={`text-2xl font-bold ${isPro ? "text-white" : "text-slate-900 dark:text-foreground/80 dark:text-white"}`}>{t("licenses.customPrice")}</p>
                        <p className={`text-xs mt-0.5 ${isPro ? "text-white/60" : "text-slate-400"}`}>{t("licenses.contactForPricing")}</p>
                      </div>
                    ) : plan.isFree ? (
                      <div>
                        <p className={`text-3xl font-bold ${isPro ? "text-white" : "text-slate-900 dark:text-foreground/80 dark:text-white"}`}>{t("licenses.freePrice")}</p>
                        <p className={`text-xs mt-0.5 ${isPro ? "text-white/60" : "text-slate-400"}`}>{t("licenses.noCreditCard")}</p>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className={`text-xs font-semibold ${isPro ? "text-white/70" : "text-slate-400"}`}>{currency}</span>
                        <span className={`text-4xl font-extrabold tracking-tight ${isPro ? "text-white" : "text-slate-900 dark:text-foreground/80 dark:text-white"}`}>{price}</span>
                        <span className={`text-sm ${isPro ? "text-white/60" : "text-slate-400"}`}>/yr</span>
                      </div>
                    )}
                  </div>

                  {/* CTA Button */}
                  <div className="mb-6">
                    {plan.isFree ? (
                      <button className={`plan-btn w-full py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-95
                        ${isPro ? "bg-card/20 text-white hover:bg-card/30" : "border-2 border-slate-200 dark:border-border dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                        {t("licenses.startFree")}
                      </button>
                    ) : plan.isBusiness ? (
                      <button className={`plan-btn w-full py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-95
                        ${isPro ? "bg-card text-primary hover:bg-card/90 shadow-lg shadow-black/10" : "bg-gradient-to-r from-purple-500 to-violet-600 text-white hover:opacity-90 shadow-sm shadow-purple-200 dark:shadow-purple-900/30"}`}>
                        {t("licenses.contactSales")}
                      </button>
                    ) : (
                      <button
                        onClick={() => { setSelectedPlan(plan); setShowCheckout(true) }}
                        className={`plan-btn w-full py-3 rounded-xl text-sm font-bold transition-all active:scale-95 shadow-lg
                          ${isPro
                            ? "bg-card text-purple-600 hover:bg-card/95 hover:shadow-xl shadow-white/20"
                            : `bg-gradient-to-r ${plan.gradient} text-white hover:opacity-90 shadow-sm`
                          }`}>
                        {t("licenses.getStarted")}
                      </button>
                    )}
                  </div>

                  {/* Divider */}
                  <div className={`h-px mb-5 ${isPro ? "bg-card/20" : "bg-slate-100 dark:bg-border"}`} />

                  {/* Features */}
                  <div className="space-y-2.5 flex-1">
                    {plan.features.map((f, i) => (
                      <div key={i} className="flex items-center gap-2.5">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${isPro ? "bg-card/20" : "bg-primary/10"}`}>
                          <Check className={`w-2.5 h-2.5 ${isPro ? "text-white" : "text-primary"}`} />
                        </div>
                        <span className={`text-xs leading-relaxed ${isPro ? "text-white/80" : "text-slate-600 dark:text-slate-400"}`}>{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* ─── Add-Ons ─────────────────────────────────── */}
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white tracking-tight">{t("licenses.superchargeAddOns")}</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{t("licenses.extendPlanFeatures")}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {addOns.map((addon, idx) => (
              <div
                key={addon.id}
                className="pricing-card-animate group relative bg-card border border-stroke-100 dark:border-border dark:border-border rounded-2xl overflow-hidden shadow-xs hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                style={{ animationDelay: `${idx * 100 + 400}ms` }}
              >
                {/* Gradient header */}
                <div className={`h-24 bg-gradient-to-br ${addon.gradient} relative overflow-hidden`}>
                  <div className="absolute inset-0 bg-black/10" />
                  <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full bg-card/10" />
                  <div className={`absolute top-4 left-4 w-12 h-12 ${addon.iconBg} rounded-xl flex items-center justify-center shadow-lg`}>
                    {addon.icon}
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="text-[10px] font-bold text-white/80 bg-card/15 backdrop-blur-sm border border-white/20 px-2 py-0.5 rounded-full">
                      {t("licenses.addOn")}
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  <h3 className="font-bold text-slate-900 dark:text-foreground/80 dark:text-white text-base mb-1">{addon.name}</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed mb-4">{addon.description}</p>

                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-2xl font-extrabold text-slate-900 dark:text-foreground/80 dark:text-white">${getAddOnPrice(addon)}</span>
                      <span className="text-xs text-slate-400 ml-1">/yr</span>
                    </div>
                    <button
                      onClick={() => { setSelectedPlan({ ...addon, monthlyPrice: addon.monthlyPrice, yearlyPrice: addon.yearlyPrice }); setShowCheckout(true) }}
                      className={`flex items-center gap-1.5 text-xs font-bold text-white px-4 py-2 rounded-xl bg-gradient-to-r ${addon.gradient} hover:opacity-90 active:scale-95 transition-all shadow-sm`}
                    >
                      {t("licenses.buyNow")} <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
    </RibbonBackground>
  )
}
